/**
 * Upload utilities — shared helpers for multipart file uploads.
 * Uses raw fetch (apiClient doesn't support FormData).
 * Auth token is read from localStorage consistently.
 */

/** Get auth headers for fetch requests */
function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Exported for other modules that need auth headers (e.g. downloadFile) */
export { getAuthHeaders };

/**
 * Download a file from an authenticated endpoint via fetch+blob.
 * Triggers browser save dialog with the given filename.
 */
export async function downloadFile(
  endpoint: string,
  filename: string,
): Promise<void> {
  const res = await fetch(`/api/v1${endpoint}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    throw new Error(`Download failed: HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Upload a file via multipart FormData to the given endpoint.
 * Returns the parsed JSON response.
 */
export async function uploadFile<T = unknown>(
  endpoint: string,
  formData: FormData,
): Promise<T> {
  const res = await fetch(`/api/v1${endpoint}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: formData,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: { message: 'Upload failed' } }));
    throw new Error(body.error?.message ?? `HTTP ${res.status}`);
  }
  return res.json();
}
